import {
  Component,
  Element,
  h,
  Listen,
  Method,
  Prop,
  setErrorHandler,
  State,
  Watch,
} from "@stencil/core";
import { Content, EMPTY_CONTENT, Series } from "@phn/entities/content";
import { navContentStore } from "@phn/state/nav-content-store";
import {
  navStateDefaultData,
  navStateStore,
  NavStateType,
} from "@phn/state/nav-state-store";
import {
  goTo,
  mountRouteFromString,
  reset,
} from "../../services/routing-manager";
import { loadRootElement } from "@phn/utility/helper";
import animationManager from "../../services/animation-manager";
import windowService from "@phn/services/window-service";
import { routerStore } from "@phn/state/router-store";
import {
  shopStateStore,
  ShopStateStoreTypes,
  shopStoreDefaultData,
} from "@phn/state/shop-state-store";
import {
  finderStateStore,
  FinderStateStoreTypes,
  finderStoreDefaultData,
} from "@phn/state/finder-state-store";
import logger from "@phn/services/logger";
import {
  featureTogglesDefaultData,
  FeatureToggleState,
  featureToggleStateStore,
} from "@phn/state/feature-toggles-state";
import featureToggles from "@phn/services/feature-toggles";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import { getImage } from "@phn/utility/navi-state";
import {
  eventifyErrorHandling,
  eventifyEventHandling,
  initMicroAgent,
  navigationAgentErrorHandler,
  processErrorQueue,
  processEventQueue,
  trackFeatureToggles,
  trackNavigationDrawerLoad,
  trackNavigationLoad,
  trackSeriesImagePreLoad,
  trackSeriesImagePreLoadError,
} from "@phn/services/new-relic-service";
import { initErrorQueue } from "@phn/services/error-queue.service";
import { initEventQueue } from "@phn/services/event-queue.service";

@Component({
  tag: "phn-nd-navigation-drawer-initiater",
  styleUrl: "navigation-drawer-initiater.scss",
})
export class NavigationDrawerInitiater {
  @Prop() navContent: Content;

  @Prop() navStateContent: NavStateType;

  @Prop() shopStoreContent: ShopStateStoreTypes;

  @Prop() finderStoreContent: FinderStateStoreTypes;

  @Prop() featureToggleStateStore: FeatureToggleState;

  @Element() el!: HTMLPhnNdNavigationDrawerInitiaterElement;

  @State() isDrawerOpened = false;

  @State() imagesPreloaded = false;

  @State() isMonitoringInitialized = false;

  componentWillLoad() {
    initErrorQueue();
    initEventQueue();
    setErrorHandler(navigationAgentErrorHandler);
    windowService.drawerVersion = "VERSION_STRING";
    navContentStore.state = this.navContent || EMPTY_CONTENT;
    navStateStore.state = this.navStateContent || navStateDefaultData;
    shopStateStore.state = this.shopStoreContent || shopStoreDefaultData;
    finderStateStore.state = this.finderStoreContent || finderStoreDefaultData;
    featureToggleStateStore.state =
      this.featureToggleStateStore || featureTogglesDefaultData;

    GlobalDataLayerService.getInstance().setEnvironment(
      navStateStore.state.env,
    );

    GlobalDataLayerService.getInstance().setDefaultProperties(
      navStateStore.state.locale,
      navStateStore.state.pageName,
    );

    loadRootElement(this.el);
    animationManager.initialize();

    this.el.style.setProperty(
      "--scrollbar-width",
      windowService.getScrollbarWidth(),
    );
  }

  componentDidLoad(): void {
    try {
      loadRootElement(this.el);
      this.checkMarketingConsent();
    } catch (err) {
      logger.error("INIT_COOKIE_CONSENT", err as Error);
    }
  }

  @Listen("ucInit", { target: "window", passive: true })
  handleUserCentricInitialization(): void {
    try {
      this.checkMarketingConsent();
    } catch (err) {
      logger.error("INIT_COOKIE_CONSENT", err as Error);
    }
  }

  private checkMarketingConsent() {
    windowService.maybeCheckMarketingConsent();
    this.initNewRelic();
  }

  @Watch("navContent")
  watchNavContent(newValue: Content) {
    navContentStore.state = newValue;
    if (navContentStore.state?.models?.series?.length > 0) {
      this.preloadImagesForSeries(navContentStore.state.models.series);
    }
    if (navContentStore.state?.electric) {
      this.preloadImage(
        getImage("electric", navStateStore.state.env) as string,
      );
      this.preloadImage(getImage("charge", navStateStore.state.env) as string);
    }
  }

  @Watch("navStateContent")
  watchNavState(newValue: NavStateType) {
    navStateStore.state = { ...navStateStore.state, ...newValue };
    GlobalDataLayerService.getInstance().setEnvironment(
      navStateStore.state.env,
    );
  }

  @Watch("shopStoreContent")
  watchShopState(newValue: ShopStateStoreTypes) {
    shopStateStore.state = newValue;
  }

  @Watch("finderStoreContent")
  watchFinderState(newValue: FinderStateStoreTypes) {
    finderStateStore.state = newValue;
  }

  @Watch("featureToggleStateStore")
  watchFeatureToggleState(newValue: FeatureToggleState) {
    featureToggleStateStore.state = newValue;
  }

  @Listen("resize", { target: "window", passive: true })
  resizeHandler(): void {
    routerStore.state.breakpoint = windowService.getBreakpoint();
  }

  @Listen("keydown", { target: "window", passive: true })
  handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.isDrawerOpened) {
      this.closeDrawer();
      return;
    }
  }

  @Method()
  async openDrawer(initialRoutingKeys: string) {
    this.isDrawerOpened = true;
    goTo(...mountRouteFromString(initialRoutingKeys));
  }

  @Method()
  async closeDrawer() {
    const wasOpen = this.isDrawerOpened;
    this.isDrawerOpened = false;
    reset(wasOpen);
  }

  /**
   * For performance reasons the micro agent is lazy loaded
   * Errors have been queued until tracking starts and will be processed as events after the agent is loaded
   * @private
   */
  private initNewRelic() {
    if (this.isMonitoringInitialized || !featureToggles.isNewRelicEnabled()) {
      return;
    }

    if (navStateStore.state.userConsent.newRelic === true) {
      initMicroAgent()
        .then(() => {
          processEventQueue();
          eventifyEventHandling();
          processErrorQueue();
          eventifyErrorHandling();
          trackNavigationDrawerLoad();
          trackFeatureToggles(featureToggleStateStore.state.features);

          if (window.PHN_HEADER_VERSION) {
            trackNavigationLoad({ state: this.navStateContent });
          }
          // eslint-disable-next-line no-empty
        })
        .catch(() => {})
        .finally(() => {
          this.isMonitoringInitialized = true;
        });
    }
  }

  private preloadImagesForSeries(series: Series[]): void {
    if (this.imagesPreloaded) {
      return;
    }

    Promise.all(
      // eslint-disable-next-line @typescript-eslint/promise-function-async
      series.map((modelsSeries) =>
        this.preloadImage(
          getImage(modelsSeries.image, navStateStore.state.env) as string,
        ),
      ),
    ).then(
      () => {
        trackSeriesImagePreLoad();
        this.imagesPreloaded = true;
      },
      (e: Error) => {
        trackSeriesImagePreLoadError(e);
        this.imagesPreloaded = false;
      },
    );
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async
  private preloadImage(srcset: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const image = document.createElement("img");
      image.onload = resolve as unknown as HTMLImageElement["onload"];
      image.onerror = reject;
      image.srcset = srcset;
    });
  }

  render() {
    return <phn-nd-drawer-selector />;
  }
}
